import { PackagingPart } from 'src/app/model/packaging-part-enum';
import { DataExchangeRequestApiService } from 'src/app/data-transfer/services/data-exchange-request-api-service';
import { PermissionTypeDto } from 'src/app/data-transfer/entities/permission-type-dto';
import { ImportExportApiService } from 'src/app/data-transfer/services/import-export-api-service';
import { DirectoryApiService } from '../../../../data-transfer/services/directory-api-service';
import { TranslocoService } from '@jsverse/transloco';
import { MaterialApiService } from '../../../../data-transfer/services/material-api-service';
import { MaterialNavigationService } from '../../../../navigation/services/navigation-services/material-navigation.service';
import { OverviewTableComponent } from './../../../shared-components/parent-components/overview-table/overview-table.component';
import { MultiMaterialCompositeDto } from 'src/app/data-transfer/entities/material-entities/multi-material-composite-dto';
import { ChangeDetectorRef, Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, AfterViewInit, Inject, LOCALE_ID, ViewChild, OnInit } from '@angular/core';
import { DeleteDialogData, DeleteItemDialogComponent } from 'src/app/components/dialogs/delete-item-dialog/delete-item-dialog.component';
import { SimpleAlertDialogComponent, SimpleDialogData } from 'src/app/components/dialogs/simple-alert-dialog/simple-alert-dialog.component';
import { SelectLocationDialogComponent } from '../../dialogs/select-location-dialog/select-location-dialog.component';
import { forkJoin, of } from 'rxjs';
import { MultiMaterialCompositeImportWrapper } from 'src/app/components/dialogs/selection-dialog-materials/selection-dialog-materials.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NgxSpinnerService } from 'ngx-spinner';
import { DirectoryDto } from 'src/app/data-transfer/entities/directory-dto';
import { getDialogConfig } from 'src/app/util/dialog-util';
import { DeletedEntitiesDto, DeletedEntityDto } from 'src/app/data-transfer/entities/deleted-entities-dto';
import { DialogActions } from 'src/app/model/dictionary';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { PaginationDataSource } from 'src/app/util/pagination-datasource';
import { Sort } from '@angular/material/sort';
import { PaginationFilterDto } from 'src/app/data-transfer/entities/pagination-filter-dto';
import { MultiMaterialPaginationFields } from 'src/app/data-transfer/entities/pagination-fields';
import { DataExchangeSubRequestDto } from 'src/app/data-transfer/entities/data-exchange-request-entities/data-exchange-sub-request-dto';
import { ColumnDefinitionDto } from 'src/app/data-transfer/entities/user-profile-dto';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { CoreDataUpdateService } from 'src/app/data-transfer/services/core-data-update-api-service';

@Component({
  selector: 'app-composite-materials-overview',
  templateUrl: './composite-materials-overview.component.html',
  styleUrls: ['./composite-materials-overview.component.scss'],
})
export class CompositeMaterialsOverviewComponent extends OverviewTableComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() dataSource: PaginationDataSource<MultiMaterialCompositeImportWrapper> | MatTableDataSource<MultiMaterialCompositeImportWrapper>;
  @Input() displayedColumns: string[] = [];
  @Input() subRequestMap?: Map<number, DataExchangeSubRequestDto>;
  @Input() commentCountMap?: Map<number, number>;
  @Input() isUserValidator = false;
  @Input() isRecyclingBinSelected = false;
  @Input() directoryIds?: number[];

  @Output() dataChanged = new EventEmitter();
  @Output() navigateToSubRequest = new EventEmitter<number>();
  @Output() approveSubRequest = new EventEmitter<number>();
  @Output() reopenSubRequest = new EventEmitter<number>();
  @Output() withdrawSubRequest = new EventEmitter<number>();
  @Output() showSubRequestComments = new EventEmitter<number>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private materialIds: number[] = [];
  selectedMaterialIds: number[] = [];
  callerLevelId = PackagingPart.Material;
  isLoading = of(false);

  protected columnCatalog: ColumnDefinitionDto[] = [
    { key: 'id', labelKey: 'commonPackagingFields.id' },
    { key: 'tags', labelKey: 'dataManagement.tags.titleTags' },
    { key: 'tracking', labelKey: 'dataManagement.tracking' },
    { key: 'articleName', labelKey: 'material.fields.articleName' },
    { key: 'articleNumber', labelKey: 'material.fields.articleNumber' },
    { key: 'manufacturerName', labelKey: 'material.fields.manufacturer' },
    { key: 'gtin', labelKey: 'material.fields.gtin' },
    { key: 'manufacturingCountry', labelKey: 'material.fields.manufacturingCountry' },
    { key: 'totalWeight', labelKey: 'material.fields.totalWeight' },
    { key: 'totalGrammage', labelKey: 'material.fields.totalGrammage' },
    { key: 'hasProductImages', labelKey: 'image.productImages' },
    { key: 'hasCertificates', labelKey: 'certificate.certificates' },
    { key: 'hasAdditionalDocuments', labelKey: 'pdf.productPdfs' },
    { key: 'creationTimestamp', labelKey: 'commonPackagingFields.creationDate' },
    { key: 'lastModificationTimestamp', labelKey: 'commonPackagingFields.lastModificationDate' },
    { key: 'action', labelKey: 'dataManagement.actions' },
  ];
  protected columnSettingsTableType = PackagingPart[PackagingPart.Material];
  protected entityLabelKey = 'material.compositeMaterial';

  constructor(
    private materialApiService: MaterialApiService,
    protected navigationService: MaterialNavigationService,
    protected directoryApiService: DirectoryApiService,
    protected importExportApiService: ImportExportApiService,
    protected dialog: MatDialog,
    protected translateService: TranslocoService,
    protected userApiService: UserApiService,
    protected coreDataUpdateService: CoreDataUpdateService,
    protected spinner: NgxSpinnerService,
    @Inject(LOCALE_ID) public locale: string,
    private dataExchangeRequestApiService: DataExchangeRequestApiService,
    private cd: ChangeDetectorRef
  ) {
    super(dialog, directoryApiService, importExportApiService, navigationService, translateService, userApiService, coreDataUpdateService, spinner, locale);
    this.dataSource = new MatTableDataSource<MultiMaterialCompositeImportWrapper>();
  }

  get isPaginatorVisible(): boolean {
    return this.dataSource instanceof PaginationDataSource;
  }

  ngOnInit(): void {
    if (this.dataSource instanceof PaginationDataSource) {
      this.isLoading = this.dataSource.isLoading;
    }
    this.loadColumnSettings();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);
    if (this.dataSource instanceof PaginationDataSource) {
      this.ngOnChangesPagination(changes);
    } else {
      this.ngOnChangesStatic(changes, this.dataSource);
    }
  }

  private ngOnChangesStatic(changes: SimpleChanges, dataSource: MatTableDataSource<MultiMaterialCompositeImportWrapper>): void {
    if (changes.dataSource != null) {
      super.setDataSourceFilter(dataSource);
      this.dataSourceSubscription = dataSource.connect().subscribe(_ => {
        this.updateDisplayTrackedColumn(dataSource);
      });
    }
  }

  private ngOnChangesPagination(changes: SimpleChanges): void {
    if (changes.dataSource != null) {
      this.selectedMaterialIds = [];
    }
    if ((changes.directoryIds || changes.isRecyclingBinSelected) && this.paginator) {
      this.refreshDataSource();
      return;
    }
  }

  ngAfterViewInit(): void {
    if (this.dataSource instanceof PaginationDataSource) {
      this.ngAfterViewInitPagination(this.dataSource);
    } else {
      this.ngAfterViewInitStatic();
    }
  }

  private ngAfterViewInitPagination(dataSource: PaginationDataSource<MultiMaterialCompositeImportWrapper>): void {
    this.refreshDataSource();
    dataSource.page.subscribe(page => {
      this.paginator.length = page.totalItemCount;
      this.paginator.pageIndex = page.pageIndex;
      this.materialIds = page.allIds;
      this.selectedMaterialIds = this.selectedMaterialIds.filter(id => page.allIds.includes(id));
    });
    dataSource.filteredColumns.subscribe(filteredColumns => {
      this.filteredColumns = filteredColumns;
    });
    this.cd.detectChanges();
  }

  private ngAfterViewInitStatic() {
    this.dataSource.sort = this.sort;
    if (this.dataSource instanceof MatTableDataSource) {
      this.dataSource.sortingDataAccessor = (data: MultiMaterialCompositeImportWrapper, sortHeaderLabel: string) => {
        if (sortHeaderLabel === 'subRequestStatus') {
          const subRequestStatus = data.id != null ? this.subRequestMap?.get(data.id)?.status : undefined;
          const label = subRequestStatus != null ? this.getSubRequestStatusLabel(subRequestStatus) : '';
          return this.translateService.translate(label).toLowerCase();
        }

        const value = data[sortHeaderLabel as keyof MultiMaterialCompositeImportWrapper];
        if (typeof value === 'string') {
          return value.toLowerCase();
        }
        if (typeof value === 'number') {
          return value;
        }

        return '';
      };
    }
  }

  protected getFilterValue(data: MultiMaterialCompositeImportWrapper, key: string): any {
    if (key === 'subRequestStatus') {
      const subRequestStatus = data.id != null ? this.subRequestMap?.get(data.id)?.status : undefined;
      const label = subRequestStatus != null ? this.getSubRequestStatusLabel(subRequestStatus) : '';
      return this.translateService.translate(label);
    }

    return super.getFilterValue(data, key);
  }

  private refreshDataSource() {
    if (!(this.dataSource instanceof PaginationDataSource)) {
      return;
    }
    if (!this.isRecyclingBinSelected && this.directoryIds != null && this.directoryIds.length === 0) {
      this.dataSource.clear();
      return;
    }

    let filters: PaginationFilterDto[] = [];

    if (!this.isRecyclingBinSelected && this.directoryIds && this.directoryIds.length > 0) {
      filters = [{ fieldName: 'DirectoryId', values: this.directoryIds.map(id => id.toString()) }];
    }

    this.dataSource.fetchData(this.paginator.pageIndex, this.paginator.pageSize, filters);
  }

  navigateToMaterial(material: MultiMaterialCompositeDto) {
    if (material.id == null) {
      return;
    }
    this.navigationService.navigateToMaterial(material.id);
  }

  movePackagingPart(rootFolder: DirectoryDto, unreachableFolders: DirectoryDto[]) {
    const data = { rootFolder, unreachableFolders };
    const dialogConfig = getDialogConfig(data, '500px');
    const dialogRef = this.dialog.open(SelectLocationDialogComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.REJECT) {
        return;
      }
      const observables = this.selectedMaterialIds
        .filter(materialId => materialId != null)
        .map(materialId => {
          return this.directoryApiService.moveMaterial(result.data.id, materialId as number);
        });
      this.moveBackendSubscription = forkJoin(observables).subscribe(_ => {
        this.dataChanged.emit();
        this.refreshDataSourceAfterChange(value => this.selectedMaterialIds.includes(value.id ?? -1));
      });
    });
  }

  copyPackagingPart(rootFolder: DirectoryDto, unreachableFolders: DirectoryDto[]) {
    const data = { rootFolder, unreachableFolders };
    const dialogConfig = getDialogConfig(data, '500px');
    const dialogRef = this.dialog.open(SelectLocationDialogComponent, dialogConfig);
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.REJECT) {
        return;
      }
      const observables = this.selectedMaterialIds
        .filter(m => m != null)
        .map(materialId => {
          return this.directoryApiService.copyMaterial(result.data.id, materialId as number);
        });
      this.copyBackendSubscription = forkJoin(observables).subscribe({
        next: _ => this.dataChanged.emit(),
        error: _ => this.spinner.hide(),
      });
    });
  }

  deletePackagingPart(itemsToDelete = this.selectedMaterialIds) {
    const observables = itemsToDelete
      .filter(materialId => materialId != null)
      .map(materialId => {
        return this.materialApiService.getMaterialPermissions(materialId);
      });
    this.permissionsSubscription = forkJoin(observables).subscribe((permissionsList: PermissionTypeDto[]) => {
      if (permissionsList.filter(x => x?.delete === false).length > 0) {
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getDeleteNotAllowedData(this.translateService), '500px'));
      } else {
        this.openDeleteDialog(itemsToDelete);
      }
    });
  }
  
  exportPackagingPart() {
    this.exportService.exportPackagingParts(
      PackagingPart.Material,
      this.selectedMaterialIds
    );

    this.resetSelection();
  }

  private openDeleteDialog(materialIds: number[]) {
    const dialogData: DeleteDialogData = {
      dialogHeader: this.translateService.translate('material.deleteMaterialHeader'),
      dialogText: this.translateService.translate('material.deleteMaterialsText'),
    };
    const dialogRef = this.dialog.open(DeleteItemDialogComponent, getDialogConfig(dialogData, '500px'));
    this.dialogSubscription = dialogRef.afterClosed().subscribe(result => {
      if (result.event === DialogActions.DELETE) {
        this.deleteMaterialsBulk(materialIds);
      }
    });
  }

  emptyRecyclingBin() {
    if (!this.isRecyclingBinSelected) {
      console.log('PackagingUnitsOverviewComponent: Recycling bin must be selected prior to emptying.');
      return;
    }
    this.deleteMaterialsBulk(this.dataSource.data.filter(x => x.id).map(x => x.id ?? -1));
  }

  private refreshDataSourceAfterChange(filterPredicate: (value: MultiMaterialCompositeImportWrapper) => boolean | undefined) {
    if (this.dataSource instanceof PaginationDataSource) {
      this.refreshDataSource();
    } else {
      this.dataSource.data = this.dataSource.data.filter(filterPredicate);
    }
  }

  private deleteMaterialsBulk(materials: number[]) {
    this.spinner.show();
    const deletePermamently = this.isRecyclingBinSelected;
    const materialsToDelete: DeletedEntityDto[] = materials.map(x => ({ id: x, version: 0 }));
    const toDeleteDto: DeletedEntitiesDto = {
      deleted: materialsToDelete,
    };
    this.deleteBackendSubscription = this.materialApiService.deleteCompositeMaterials(toDeleteDto, deletePermamently, true).subscribe({
      next: _ => {
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(this.getDeleteMaterialsSuccessData(), '500px'));
        this.refreshDataSourceAfterChange(value => {
          if (value.id != null) {
            return !toDeleteDto.deleted.map(x => x.id).includes(value.id);
          }
        });
        this.spinner.hide();
      },
      error: e => {
        let errorData;
        if (e.status === 403) {
          errorData = this.getDeletionImpossibleData();
        } else {
          errorData = this.getDeleteMaterialsErrorData();
        }
        this.dialog.open(SimpleAlertDialogComponent, getDialogConfig(errorData, '500px'));
        this.spinner.hide();
      },
    });
  }

  restorePackagingPart() {
    this.restoreMaterials(this.selectedMaterialIds);
  }

  restoreAllMaterials() {
    this.restoreMaterials(this.dataSource.data.map(x => x.id ?? -1));
  }

  private restoreMaterials(materialIds: number[]) {
    if (!this.isRecyclingBinSelected) {
      console.log('PackagingComponentsOverviewComponent: Recycling bin must be selected prior to restoring.');
      return;
    }
    for (const materialId of materialIds) {
      this.refreshDataSourceAfterChange(value => {
        return value.id !== materialId;
      });
      this.restoreSubscription = this.directoryApiService.restoreMaterialFromRecyclingBin(materialId)?.subscribe(_ => {
        window.location.reload();
      });
    }
  }

  getTagsSettingFunction(isEditing: boolean) {
    return (id: number, tagIds: number[]) =>
      isEditing ? this.materialApiService.setCompositeMaterialTags(id, tagIds) : this.materialApiService.addCompositeMaterialTags(id, tagIds);
  }

  getPackagingPartFunction() {
    return (id: number) => this.materialApiService.getCompositeMaterial(id);
  }

  comparePackagingPart() {}

  editPackagingPartQuantity() {}

  createDataExchangeRequest(): void {
    this.openCreateDataExchangeRequestDialog({ allPrincipals: [], materialIds: this.selectedMaterialIds }, dto =>
      this.dataExchangeRequestApiService.createDataExchangeRequest(dto, true)
    );
    this.resetSelection();
  }

  resetSelection() {
    this.selectedMaterialIds = [];
  }

  selectAllMaterials() {
    this.allItemsSelected = !this.allItemsSelected;
    this.selectedMaterialIds = [];
    if (this.allItemsSelected) {
      for (const multiMaterialId of this.materialIds) {
        this.selectedMaterialIds.push(multiMaterialId);
      }
    }
    this.packagingPartsSelected.emit(this.selectedMaterialIds);
  }

  isMaterialSelected(item: MultiMaterialCompositeDto): boolean {
    if (this.selectedMaterialIds && this.selectedMaterialIds.length > 0) {
      return this.selectedMaterialIds.includes(item.id ?? -1);
    }
    return false;
  }

  setMaterialSelected(checked: boolean, item: MultiMaterialCompositeDto) {
    if (checked && item.id) {
      this.selectedMaterialIds.push(item.id);
    } else {
      const index = this.selectedMaterialIds.indexOf(item.id ?? -1);
      this.selectedMaterialIds.splice(index, 1);
    }
    this.packagingPartsSelected.emit(this.selectedMaterialIds);
  }

  downloadMultiMaterialsJson() {
    const multiMaterialsToDownload: { id: number; version: number }[] = [];
    this.selectedMaterialIds.forEach(x => {
      if (x != null) {
        multiMaterialsToDownload.push({ id: x, version: 0 });
      }
    });
    this.jsonSubscription = this.materialApiService.downloadMultiMaterialsJson(multiMaterialsToDownload).subscribe(zipFile => {
      const zipUrl = window.URL.createObjectURL(zipFile);
      window.open(zipUrl, '_blank', 'noreferrer');
    });
  }

  private getDeleteMaterialsSuccessData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.success'),
      messages: [this.translateService.translate('dataManagement.directory.dialog.removal.materialsDeletedSuccess')],
      icon: 'info',
    };
  }

  private getDeletionImpossibleData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.error'),
      messages: [this.translateService.translate('material.messages.materialInUse')],
      icon: 'error',
    };
  }

  private getDeleteMaterialsErrorData(): SimpleDialogData {
    return {
      title: this.translateService.translate('common.text.error'),
      messages: [this.translateService.translate('dataManagement.directory.dialog.removal.materialsDeletedError')],
      icon: 'error',
    };
  }

  onPageChanged(event: PageEvent) {
    if (this.dataSource instanceof PaginationDataSource) {
      this.dataSource.fetchData(event.pageIndex, event.pageSize);
    }
  }

  onSortChanged(event: Sort) {
    if (this.dataSource instanceof PaginationDataSource) {
      if (event.direction) {
        this.dataSource.sort(this.getPaginationFieldName(event.active), event.direction == 'asc');
      } else {
        this.dataSource.removeSorting();
      }
    }
  }

  getPaginationFieldName(column: string) {
    switch (column) {
      case 'id':
        return MultiMaterialPaginationFields.Id;
      case 'tags':
        return MultiMaterialPaginationFields.Tag;
      case 'articleName':
        return MultiMaterialPaginationFields.ArticleName;
      case 'articleNumber':
        return MultiMaterialPaginationFields.ArticleNumber;
      case 'manufacturerName':
        return MultiMaterialPaginationFields.ManufacturerName;
      case 'gtin':
        return MultiMaterialPaginationFields.Gtin;
      case 'manufacturingCountry':
        return MultiMaterialPaginationFields.ManufacturingLocation;
      case 'totalWeight':
        return MultiMaterialPaginationFields.TotalWeight;
      case 'totalGrammage':
        return MultiMaterialPaginationFields.TotalGrammage;
      case 'creationTimestamp':
        return MultiMaterialPaginationFields.CreationTimestamp;
      case 'lastModificationTimestamp':
        return MultiMaterialPaginationFields.LastModificationTimestamp;
      case 'hasProductImages':
        return MultiMaterialPaginationFields.HasImages;
      case 'hasCertificates':
        return MultiMaterialPaginationFields.HasCertificates;
      case 'hasAdditionalDocuments':
        return MultiMaterialPaginationFields.HasAdditionalDocuments;
    }

    return '';
  }
  getStatusOptions(): { name: string; value: string }[] {
    if (!this.subRequestMap) {
      return [];
    }
    const labels = [...this.subRequestMap.values()].map(s => this.translateService.translate(this.getSubRequestStatusLabel(s.status))).filter(Boolean) as string[];
    return [...new Set(labels)].sort().map(label => ({ name: label, value: label }));
  }

  getCaller(columnName: string) {
    if (this.isPaginatorVisible) {
      return this.getPaginationFieldName(columnName);
    }

    return columnName;
  }
}
